import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Button } from "components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";

const promises = [
  {
    id: "visits",
    label: "At your property",
    title: "We know your property.",
    description:
      "A minimum of four property visits every year, at no additional charge. Owners and committee members are welcome to join us wherever possible.",
  },
  {
    id: "communication",
    label: "Clear communication",
    title: "Clarity comes as standard.",
    description:
      "Plain language, concise information and a professional, courteous response whenever you contact us. So you can understand the details and make informed decisions.",
  },
  {
    id: "updates",
    label: "Keeping you updated",
    title: "You’re kept in the loop.",
    description:
      "We aim to respond to calls and emails within two business days. If an enquiry is still being resolved, we aim to provide a progress update within three working days.",
    milestones: [
      { value: "2", unit: "business days", detail: "Our response target" },
      {
        value: "3",
        unit: "working days",
        detail: "A progress update if unresolved",
      },
    ],
  },
  {
    id: "repairs",
    label: "Following through",
    title: "We see things through.",
    description:
      "We aim to arrange repairs within two working days, or immediately where required and possible. We also review contracts and service providers every year.",
    milestones: [
      {
        value: "2",
        unit: "working days",
        detail: "Our target to arrange repairs",
      },
      {
        value: "Every",
        unit: "year",
        detail: "Contracts and providers reviewed",
      },
    ],
  },
  {
    id: "meetings",
    label: "Meetings that matter",
    title: "Meetings with a purpose.",
    description:
      "We aim to organise your AGM within three months of the financial year end, or as agreed with your committee, and supply minutes within 14 working days.",
    milestones: [
      {
        value: "3",
        unit: "months",
        detail: "AGM target after financial year end",
      },
      {
        value: "14",
        unit: "working days",
        detail: "Our target for meeting minutes",
      },
    ],
  },
];

function VisitsIllustration() {
  return (
    <figure className="site-charter-visits">
      <div className="site-charter-arches" aria-hidden="true">
        {[1, 2, 3, 4].map((visit) => (
          <div
            className="site-charter-arch"
            key={visit}
            style={{ "--visit": visit }}
          >
            <span className="site-charter-window" />
            <span className="site-charter-visit-mark">
              <Check />
            </span>
          </div>
        ))}
      </div>
      <figcaption>Four visits a year. At a minimum.</figcaption>
    </figure>
  );
}

function PromiseDetail({ promise }) {
  if (promise.id === "visits") return <VisitsIllustration />;
  if (promise.id === "communication") {
    return (
      <ul className="site-charter-clarity">
        {["Plain language.", "Useful detail.", "Straight answers."].map(
          (line) => (
            <li key={line}>
              <span>{line}</span>
              <Check aria-hidden="true" />
            </li>
          ),
        )}
      </ul>
    );
  }
  return (
    <ul className="site-charter-milestones">
      {promise.milestones.map(({ value, unit, detail }) => (
        <li key={detail}>
          <strong>
            {value}
            <span>{unit}</span>
          </strong>
          <p>{detail}</p>
        </li>
      ))}
    </ul>
  );
}

export default function ServiceCharter() {
  const [active, setActive] = useState("visits");
  const [compact, setCompact] = useState(
    () => window.matchMedia("(max-width: 850px)").matches,
  );
  const tabList = useRef(null);
  const focusPanel = useRef(false);
  const focusHeading = useCallback(
    (heading) => {
      if (heading && focusPanel.current) {
        heading.focus({ preventScroll: !compact });
        focusPanel.current = false;
      }
    },
    [compact],
  );

  useEffect(() => {
    const query = window.matchMedia("(max-width: 850px)");
    const update = () => setCompact(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (compact && tabList.current) {
      const list = tabList.current;
      const tab = list.querySelector('[data-state="active"]');
      if (tab) {
        const listBounds = list.getBoundingClientRect();
        const tabBounds = tab.getBoundingClientRect();
        if (
          tabBounds.left < listBounds.left ||
          tabBounds.right > listBounds.right
        ) {
          list.scrollTo({
            left:
              list.scrollLeft +
              tabBounds.left -
              listBounds.left -
              (list.clientWidth - tabBounds.width) / 2,
            behavior: "instant",
          });
        }
      }
    }
  }, [active, compact]);

  function movePromise(direction) {
    const index = promises.findIndex((promise) => promise.id === active);
    focusPanel.current = true;
    setActive(
      promises[(index + direction + promises.length) % promises.length].id,
    );
  }

  return (
    <section
      id="our-promise"
      className="site-surface site-section site-charter"
      aria-labelledby="charter-heading"
    >
      <div className="site-wrap">
        <div className="site-charter-heading">
          <h2 id="charter-heading">
            Here’s what
            <br />
            you can expect.
          </h2>
          <p>
            Explore our service commitments, from regular site visits to keeping
            your committee informed.
          </p>
        </div>
        <Tabs
          value={active}
          onValueChange={setActive}
          orientation={compact ? "horizontal" : "vertical"}
          className="site-charter-layout"
        >
          <TabsList
            ref={tabList}
            variant="charter"
            aria-label="Our service commitments"
            className="site-charter-tabs"
          >
            {promises.map(({ id, label }) => (
              <TabsTrigger
                variant="charter"
                value={id}
                key={id}
                className="site-charter-tab"
              >
                <span>{label}</span>
                <ArrowRight aria-hidden="true" />
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="site-charter-panel">
            {promises.map((promise) => (
              <TabsContent
                value={promise.id}
                key={promise.id}
                className="tw-mt-0 site-charter-content"
              >
                <div className="site-charter-copy">
                  <h3
                    ref={promise.id === active ? focusHeading : undefined}
                    tabIndex={-1}
                  >
                    {promise.title}
                  </h3>
                  <p>{promise.description}</p>
                </div>
                <PromiseDetail promise={promise} />
              </TabsContent>
            ))}
            <div className="site-charter-controls">
              <Button
                variant="outline"
                size="icon"
                onClick={() => movePromise(-1)}
                aria-label="Previous promise"
              >
                <ArrowLeft />
              </Button>
              <Button variant="secondary" onClick={() => movePromise(1)}>
                Next promise
                <ArrowRight data-icon="inline-end" />
              </Button>
            </div>
          </div>
        </Tabs>
      </div>
    </section>
  );
}
